import axios, { AxiosError, AxiosResponse } from "axios";
import { Game } from "../models/Game";
import { toast } from "react-toastify";
import { store } from "../stores/store";
import { ResetPasswordData, SocialLoginData, User, UserFormValues } from "../models/User";
import { UserStatusInGameDTO } from "../models/UserStatusInGameDTO";
import { Profile, UserGame } from "../models/Profile";
import { PaginatedResult } from "../models/Pagination";
import { UpdateLocationDto } from "../models/UpdateLocationDto";
import { GameFollowingDTO } from "../models/GameFollowingDTO";
import { GameLockDTO } from "../models/GameLockDTO";
import { NotificationTokenDTO } from "../models/NotificationTokenDTO";
import { Company } from "../models/Company";
import { CompanySportVenue } from "../models/CompanySportVenue";
import { CompanySportVenueAsset, DiscountRule, PartitionRule, PricingRule } from "../models/CompanySportVenueAsset";
import { AvailabilityCheckParams, AvailabilityCheckResult, BookingPriceCalculation, BookingPriceResult, CalendarDataDTO, CalendarEventDTO, CalendarEventQueryParams, CalendarStatusLogDTO, CreateCalendarEventDTO, CreateRecurringSeriesDTO, CreateRecurringSeriesResultDTO, DeleteRecurringSeriesResultDTO, EditRecurringSeriesResultDTO, UpdateCalendarEventDTO, UpdateRecurringSeriesDTO, UpdateStatusRequest } from "../models/Calendar";
import { AvailableSlotsRequestDTO, AvailableSlotsResponseDTO } from "../models/AvailableGameSlots";
import { LanguageDTO } from "../models/LanguageDTO";
import { Friend } from "../models/Friend";
import { FriendRequest, FriendshipStatus } from "../models/FriendRequest";
import { FriendDto } from "../models/FriendDto";
import { MessageDTO } from "../stores/messageStore";
import { SubmitRatingDto, UpdateCommentApprovalDto, UserRatingResponse } from "../models/UserRatingModels";
import { NotificationChannel, NotificationEventType, NotificationPreferenceDto, UserNotificationPreference } from "../models/NotificationPreference";
import { GameInvitation, InvitableFriend } from "../models/GameInvitation";
import { AvailableCourtSlotsRequestDTO, AvailableCourtSlotsResponseDTO } from "../models/AvailableCourtSlots";
import { AddGameVideoLinkDTO, GameVideoLink } from "../models/GameVideoLink";
import { BookingsOverviewReportDTO, ClientsReportDTO, LostRevenueReportDTO, OccupancyReportDTO, PaymentsReportDTO, ReportQueryParams, RevenueReportDTO } from "../models/Reporting";
import {
    FeedItem, FeedParams, UrgencyParams, NewsItem, InfluenceProfile,
    CreatePostDto, CreateCommentDto, FeedComment
} from '../models/Playground';
import { InvitableUserDto, PendingChallengeDto, TeamDto, TeamInvitationDto, TeamLinkDto, TeamMatchDto, TeamPostDto, TeamPostReplyDto, TeamSearchResultDto } from "../models/Team";
import {
    CreateTournamentDto,
    TournamentDto,
    TournamentMatchDto,
    TournamentPostDto,
    TournamentRegistrationDto,
    TournamentRefereeDto,
    TournamentSearchParams,
    TournamentSearchResultDto,
    TournamentStandingRow,
    MyTournamentNotificationDto,
} from '../models/Tournament';
const sleep = (delay: number) => {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    });
};



// DYNAMIC BASE URL CONFIGURATION
const getBaseURL = () => {
    // Check for explicit environment variable first
    if (process.env.REACT_APP_API_URL) {
        return process.env.REACT_APP_API_URL;
    }

    // Check if we're in production
    if (process.env.NODE_ENV === 'production') {
        return '/api'; // Use nginx proxy in production/Docker
    }

    // Development mode - check if we're in Docker or local
    const currentHost = window.location.hostname;
    const isDocker = window.location.port === '80' || window.location.port === '';

    if (isDocker) {
        // Running in Docker development
        return '/api';
    } else {
        // Local development - point directly to .NET API server
        return 'http://localhost:5000/api'; // Adjust this port to match your .NET API
    }
};

// Set the base URL dynamically
axios.defaults.baseURL = getBaseURL();

// Log the base URL for debugging

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

const setupInterceptors = (navigate: (path: string, options?: { state?: any }) => void) => {
    if (requestInterceptorId !== null) {
        axios.interceptors.request.eject(requestInterceptorId);
    }
    if (responseInterceptorId !== null) {
        axios.interceptors.response.eject(responseInterceptorId);
    }

    // Request interceptor with better token handling
    requestInterceptorId = axios.interceptors.request.use(config => {
        const token = window.localStorage.getItem('jwt');

        // Define paths that should NEVER have tokens - Fixed the path matching
        const anonymousPaths = [
            'account/forgot-password',
            'account/reset-password',
            'account/login',
            'account/register'
        ];

        // Check if this request should be anonymous - Fixed the URL matching logic
        const isAnonymousRequest = anonymousPaths.some(path => {
            const fullUrl = config.url || '';
            const baseUrl = config.baseURL || '';
            const completeUrl = fullUrl.startsWith('http') ? fullUrl : `${baseUrl}/${fullUrl}`.replace(/\/+/g, '/');
            return completeUrl.includes(path) || fullUrl.includes(path);
        });

        // Only add token if we have one and the request isn't anonymous
        if (token && !isAnonymousRequest) {
            // Validate token before using it
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const currentTime = Math.floor(Date.now() / 1000);

                if (payload.exp > currentTime) {
                    // Token is valid, add it
                    config.headers.Authorization = `Bearer ${token}`;
                } else {
                    // Token is expired, clean up but don't fail the request
                    localStorage.removeItem('jwt');
                    localStorage.removeItem('user');

                    // Don't add the expired token to the request
                    // Let the response interceptor handle the navigation
                }
            } catch (error) {
                // Invalid token format, clean up
                localStorage.removeItem('jwt');
                localStorage.removeItem('user');
            }
        }

        return config;
    });

    responseInterceptorId = axios.interceptors.response.use(
        async response => {
            const pagination = response.headers['pagination'];
            if (pagination) {
                response.data = new PaginatedResult(response.data, JSON.parse(pagination));
                return response as AxiosResponse<PaginatedResult<any>>;
            }
            return response;
        },
        (error: AxiosError) => {
            const { data, status, config } = error.response!;

            // Define a custom interface for your error response data
            interface ErrorResponse {
                message?: string;
                errors?: Record<string, string[]>;
                [key: string]: any;
            }

            // Cast data to your custom interface
            const errorData = data as ErrorResponse;

            // Skip error handling for password reset endpoints when user is not logged in
            const isPasswordResetEndpoint = config?.url?.includes('reset-password') || config?.url?.includes('forgot-password');

            switch (status) {
                case 400:
                    if (
                        config.method === 'get' &&
                        data &&
                        typeof data === 'object' &&
                        'errors' in data &&
                        typeof data.errors === 'object' &&
                        data.errors &&
                        data.errors.hasOwnProperty('ID')
                    ) {
                        navigate('/not-found');
                        break;
                    }

                    if (data && typeof data === 'object' && 'errors' in data) {
                        const modalStateErrors = [];
                        const errors = (data as { errors: Record<string, string[]> }).errors;

                        for (const key in errors) {
                            if (errors[key]) {
                                modalStateErrors.push(errors[key]);
                            }
                        }
                        throw modalStateErrors.flat();
                    }
                    else {
                        toast.error(JSON.stringify(data).replace(/^"(.+(?="$))"$/, '$1'));
                    }
                    break;
                case 401: {
                    const currentPath = window.location.pathname;
                    const isOnResetPasswordPage = currentPath.includes('/account/reset-password');
                    const isOnLoginPage = currentPath === '/login'; // ← DODAJ OVO

                    if (isPasswordResetEndpoint || isOnResetPasswordPage) {
                        break;
                    }

                    const token = localStorage.getItem('jwt');
                    const msg = errorData.message?.toLowerCase() || error.message?.toLowerCase() || '';

                    if (token) {
                        try {
                            const payload = JSON.parse(atob(token.split('.')[1]));
                            const currentTime = Math.floor(Date.now() / 1000);
                            if (payload.exp > currentTime) {
                                return Promise.reject(error);
                            }
                        } catch { }
                    }

                    if (token && msg.includes('expired')) {
                        toast.error('Your session has expired. Please login again.');
                        localStorage.removeItem('jwt');
                        localStorage.removeItem('user');
                    } else if (token) {
                        toast.error('Unauthorized access.');
                    }

                   
                    if (isOnLoginPage) {
                        break;
                    }

                    const publicPaths = ['/', '/login', '/server-error', '/account/reset-password'];
                    const isPublicPath = publicPaths.some(p => currentPath.startsWith(p));

                    navigate('/login', {
                        state: { from: isPublicPath ? '/playground' : currentPath + window.location.search }
                    });
                    break;
                }
                case 404:
                    navigate('/not-found');
                    break;
                case 500:
                    store.commonStore.setServerError(data);
                    navigate('/server-error');
                    break;
                default:
                    toast.error('Something went wrong');
                    break;
            }
            return Promise.reject(error);
        }
    );


};

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
    get: <T>(url: string, config?: object) => axios.get<T>(url, config).then(responseBody),
    post: <T>(url: string, body: {}) => axios.post<T>(url, body).then(responseBody),
    put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
    patch: <T>(url: string, body: {}) => axios.patch<T>(url, body).then(r => r.data),
    postForm: <T>(url: string, data: Record<string, any>) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            if (value instanceof File) {
                formData.append(key, value, value.name);
            } else if (value !== undefined && value !== null) {
                formData.append(key, String(value));
            }
        });
        return axios.post<T>(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(responseBody);
    },
};

// Anonymous requests helper - Create a separate axios instance for anonymous requests
const anonymousAxios = axios.create({
    baseURL: getBaseURL() // Use the same dynamic base URL
});

const anonymousRequests = {
    get: <T>(url: string) => anonymousAxios.get<T>(url).then(responseBody),
    post: <T>(url: string, body: {}) => anonymousAxios.post<T>(url, body).then(responseBody),
    put: <T>(url: string, body: {}) => anonymousAxios.put<T>(url, body).then(responseBody),
    del: <T>(url: string) => anonymousAxios.delete<T>(url).then(responseBody),
};

const Games = {
    list: (params: URLSearchParams) => axios.get<PaginatedResult<Game[]>>('/games', { params }).then(responseBody),
    details: (id: string) => requests.get<Game>(`/games/${id}`),
    create: (game: Game) => requests.post<void>('/games', game),
    update: (game: Game) => requests.put<void>(`/games/${game.id}`, game),
    delete: (id: string) => requests.del<void>(`/games/${id}`),
    attend: (id: string, userStatusInGame: UserStatusInGameDTO) => requests.post<void>(`/games/${id}/attend`, userStatusInGame),
    gameFollow: (gameFollowingDTO: GameFollowingDTO) => requests.post<void>(`/gameFollow`, gameFollowingDTO),
    gameLock: (gameLockDTO: GameLockDTO) => requests.put<void>(`/gameLock/${gameLockDTO.ID}/lock`, gameLockDTO),
    findAvailableSlots: (request: AvailableSlotsRequestDTO) =>
        requests.post<AvailableSlotsResponseDTO>('/games/available-slots', request),

};

const Teams = {

    getMyTeams: () => requests.get<TeamDto[]>('/teams/my'),
    getTeam: (id: string) => requests.get<TeamDto>(`/teams/${id}`),
    createTeam: (data: { name: string; sportID: string; description?: string; latitude?: number; longitude?: number; adminPhone?: string }) =>
        requests.post<{ id: string }>('/teams', data),
    updateTeam: (id: string, data: { name?: string; description?: string; latitude?: number; longitude?: number; adminPhone?: string }) =>
        requests.put(`/teams/${id}`, data),
    updateStatus: (id: string, status: string) =>
        requests.patch(`/teams/${id}/status`, { status }),
    uploadLogo: (id: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return axios.post<{ logoUrl: string }>(`/teams/${id}/logo`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(r => r.data);
    },
    searchUsersToInvite: (id: string, q: string) =>
        requests.get<InvitableUserDto[]>(`/teams/${id}/members/search?q=${encodeURIComponent(q)}`),
    inviteMember: (id: string, userId: string) =>
        requests.post(`/teams/${id}/members/invite`, { userId }),
    removeMember: (id: string, userId: string) =>
        requests.del(`/teams/${id}/members/${userId}`),
    promoteMember: (id: string, userId: string) =>
        requests.patch(`/teams/${id}/members/${userId}/promote`, {}),
    leaveTeam: (id: string) => requests.post(`/teams/${id}/leave`, {}),
    getMyInvitations: () => requests.get<TeamInvitationDto[]>('/teams/invitations'),
    respondToInvitation: (id: string, accept: boolean) =>
        requests.post(`/teams/invitations/${id}/respond`, { accept }),
    updateLinks: (id: string, links: TeamLinkDto[]) =>
        requests.put(`/teams/${id}/links`, { links }),
    updateVisibility: (id: string, section: string, visibility: string) =>
        requests.patch(`/teams/${id}/visibility`, { section, visibility }),
    searchTeams: (params: { name?: string; sportId?: string; lat?: number; lng?: number; radius?: number }) => {
        const q = new URLSearchParams();
        if (params.name) q.append('name', params.name);
        if (params.sportId) q.append('sportId', params.sportId);
        if (params.lat) q.append('lat', String(params.lat));
        if (params.lng) q.append('lng', String(params.lng));
        if (params.radius) q.append('radius', String(params.radius));
        return requests.get<TeamSearchResultDto[]>(`/teams/search?${q.toString()}`);
    },
};

const TeamPosts =
{
    list: (contextType: string, contextId: string) =>
        requests.get<TeamPostDto[]>(`/teamposts?contextType=${contextType}&contextId=${contextId}`),
    create: (data: { contextType: string; contextId: string; content: string; visibility?: string }) =>
        requests.post<TeamPostDto>('/teamposts', data),
    delete: (id: string) => requests.del(`/teamposts/${id}`),
    pin: (id: string, pin: boolean) => requests.patch(`/teamposts/${id}/pin`, { pin }),
    addReply: (postId: string, content: string) =>
        requests.post<TeamPostReplyDto>(`/teamposts/${postId}/replies`, { content }),
    deleteReply: (replyId: string) => requests.del(`/teamposts/replies/${replyId}`),
    reactToPost: (postId: string, reactionType: string) =>
        requests.post(`/teamposts/${postId}/reactions`, { reactionType }),
    reactToReply: (replyId: string, reactionType: string) =>
        requests.post(`/teamposts/replies/${replyId}/reactions`, { reactionType }),

};

const TeamMatches =
{
    get: (id: string) => requests.get<TeamMatchDto>(`/teammatches/${id}`),
    createDirect: (data: {
        teamAId: string; teamBId: string;
        latitude?: number; longitude?: number; scheduledAt?: string
    }) => requests.post<{ id: string }>('/teammatches/direct', data),
    respond: (id: string, accept: boolean) =>
        requests.post(`/teammatches/${id}/respond`, { accept }),
    createOpen: (data: {
        teamAId: string; latitude?: number; longitude?: number; scheduledAt?: string
    }) => requests.post<{ id: string }>('/teammatches/open', data),
    getOpenChallenges: (sportId: string) =>
        requests.get<TeamMatchDto[]>(`/teammatches/open?sportId=${sportId}`),
    apply: (id: string, teamId: string) =>
        requests.post(`/teammatches/${id}/apply`, { teamId }),
    acceptApplication: (applicationId: string) =>
        requests.post(`/teammatches/applications/${applicationId}/accept`, {}),
    respondAvailability: (id: string, available: boolean) =>
        requests.post(`/teammatches/${id}/availability`, { available }),
    reportResult: (id: string, scoreA: number, scoreB: number) =>
        requests.post(`/teammatches/${id}/result`, { scoreA, scoreB }),
    confirmOrDispute: (id: string, confirm: boolean, disputeReason?: string) =>
        requests.post(`/teammatches/${id}/result/confirm`, { confirm, disputeReason }),
    getTeamMatches: (teamId: string) =>
        requests.get<TeamMatchDto[]>(`/teammatches/team/${teamId}`),
    searchOpponents: (teamId: string, q: string) =>
        requests.get<TeamSearchResultDto[]>(
            `/teammatches/search-opponents?teamId=${teamId}&q=${encodeURIComponent(q)}`
        ),
    getPendingChallenges: () =>
        requests.get<PendingChallengeDto[]>('/teammatches/pending-challenges'),
};


const Tournaments = {
    // ── CRUD ─────────────────────────────────────────────────────────────────────
    list: (params?: TournamentSearchParams): Promise<PaginatedResult<TournamentSearchResultDto[]>> =>
        requests.get('/tournaments', { params }),

    getById: (id: string): Promise<TournamentDto> =>
        requests.get(`/tournaments/${id}`),

    create: (data: CreateTournamentDto): Promise<string> =>
        requests.post('/tournaments', data),

    update: (id: string, data: Partial<CreateTournamentDto>): Promise<void> =>
        requests.put(`/tournaments/${id}`, data),

    uploadLogo: (id: string, file: File): Promise<string> => {
        const formData = new FormData();
        formData.append('File', file);
        return requests.post(`/tournaments/${id}/logo`, formData);
    },

    // ── Lifecycle ─────────────────────────────────────────────────────────────────
    publish: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/publish`, {}),

    openRegistration: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/open-registration`, {}),

    closeRegistration: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/close-registration`, {}),

    generateBracket: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/generate-bracket`, {}),

    start: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/start`, {}),

    complete: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/complete`, {}),

    cancel: (id: string, reason?: string): Promise<void> =>
        requests.post(`/tournaments/${id}/cancel`, { reason }),

    revertToDraft: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/revert-to-draft`, {}),

    revertToPublished: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/revert-to-published`, {}),

    revertToRegistrationOpen: (id: string): Promise<void> =>
        requests.post(`/tournaments/${id}/revert-to-registration-open`, {}),

    delete: (id: string): Promise<void> =>
        requests.del(`/tournaments/${id}`),

    // ── Registrations ─────────────────────────────────────────────────────────────
    register: (tournamentId: string, teamId: string): Promise<void> =>
        requests.post(`/tournaments/${tournamentId}/registrations`, { teamId }),

    withdraw: (tournamentId: string, registrationId: string): Promise<void> =>
        requests.del(`/tournaments/${tournamentId}/registrations/${registrationId}`),

    approveRegistration: (tournamentId: string, registrationId: string): Promise<void> =>
        requests.post(`/tournaments/${tournamentId}/registrations/${registrationId}/approve`, {}),

    rejectRegistration: (tournamentId: string, registrationId: string, note?: string): Promise<void> =>
        requests.post(`/tournaments/${tournamentId}/registrations/${registrationId}/reject`, { note }),

    updateSeed: (tournamentId: string, registrationId: string, seed: number): Promise<void> =>
        requests.put(`/tournaments/${tournamentId}/registrations/${registrationId}/seed`, { seed }),

    assignGroup: (tournamentId: string, registrationId: string, groupNumber: number): Promise<void> =>
        requests.put(`/tournaments/${tournamentId}/registrations/${registrationId}/group`, { groupNumber }),

    advanceWinner: (matchId: string, winnerId: string): Promise<void> =>
        requests.post(`/tournamentmatches/${matchId}/advance`, { winnerId }),

    // ── Matches ───────────────────────────────────────────────────────────────────
    getMatches: (tournamentId: string): Promise<TournamentMatchDto[]> =>
        requests.get(`/tournaments/${tournamentId}/matches`),

    getMatch: (matchId: string): Promise<TournamentMatchDto> =>
        requests.get(`/tournamentmatches/${matchId}`),

    updateMatchSchedule: (matchId: string, scheduledAt: string, notes?: string): Promise<void> =>
        requests.put(`/tournamentmatches/${matchId}/schedule`, { scheduledAt, notes }),

    reportResult: (matchId: string, scoreA: number, scoreB: number): Promise<void> =>
        requests.post(`/tournamentmatches/${matchId}/result`, { scoreA, scoreB }),

    confirmResult: (matchId: string): Promise<void> =>
        requests.post(`/tournamentmatches/${matchId}/result/confirm`, {}),

    disputeResult: (matchId: string, reason: string): Promise<void> =>
        requests.post(`/tournamentmatches/${matchId}/result/dispute`, { reason }),

    resolveDispute: (matchId: string, scoreA: number, scoreB: number, note: string): Promise<void> =>
        requests.post(`/tournamentmatches/${matchId}/result/resolve`, { scoreA, scoreB, note }),

    declareWalkover: (matchId: string, winnerId: string, reason: string): Promise<void> =>
        requests.post(`/tournamentmatches/${matchId}/walkover`, { winnerId, reason }),

    setMatchTeams: (matchId: string, teamAId: string | null, teamBId: string | null): Promise<void> =>
        requests.put(`/tournamentmatches/${matchId}/teams`, { teamAId, teamBId }),

    updateMatchResult: (matchId: string, scoreA: number, scoreB: number) =>
        requests.put(`tournamentmatches/${matchId}/result`, { scoreA, scoreB }),

    // ── Standings ────────────────────────────────────────────────────────────────
    getStandings: (tournamentId: string): Promise<TournamentStandingRow[]> =>
        requests.get(`/tournaments/${tournamentId}/standings`),

    // ── Referees ─────────────────────────────────────────────────────────────────
    getReferees: (tournamentId: string): Promise<TournamentRefereeDto[]> =>
        requests.get(`/tournaments/${tournamentId}/referees`),

    inviteReferee: (tournamentId: string, userId: string): Promise<void> =>
        requests.post(`/tournaments/${tournamentId}/referees/invite`, { userId }),


    respondRefereeInvite: (tournamentId: string, accept: boolean): Promise<void> =>
        requests.post(`/tournaments/${tournamentId}/referees/respond`, { accept }),

    approveReferee: (tournamentId: string, refereeId: string): Promise<void> =>
        requests.post(`/tournaments/${tournamentId}/referees/${refereeId}/approve`, {}),

    removeReferee: (tournamentId: string, refereeId: string): Promise<void> =>
        requests.del(`/tournaments/${tournamentId}/referees/${refereeId}`),

    assignRefereeToMatch: (matchId: string, userId: string): Promise<void> =>
        requests.post(`/tournamentmatches/${matchId}/referees`, { userId }),

    removeRefereeFromMatch: (matchId: string, userId: string): Promise<void> =>
        requests.del(`/tournamentmatches/${matchId}/referees/${userId}`),

    applyAsReferee: (tournamentId: string) =>
        axios.post<void>(`/tournaments/${tournamentId}/referees/apply`),

    // ── Co-Organizers ─────────────────────────────────────────────────────────────
    addCoOrganizer: (tournamentId: string, userId: string): Promise<void> =>
        requests.post(`/tournaments/${tournamentId}/co-organizers`, { userId }),

    removeCoOrganizer: (tournamentId: string, userId: string): Promise<void> =>
        requests.del(`/tournaments/${tournamentId}/co-organizers/${userId}`),

    myNotifications: (): Promise<MyTournamentNotificationDto[]> =>
        requests.get('/tournaments/mine/notifications'),

    searchUsersToInvite: (tournamentId: string, query: string): Promise<Array<{
        userId: string;
        fullName: string;
        imageUrl?: string;
        isAlreadyReferee: boolean;
        isAlreadyCoOrganizer: boolean;
    }>> =>
        requests.get(`/tournaments/${tournamentId}/users/search`, { params: { query } }),

    // ── Posts (async messaging) ───────────────────────────────────────────────────
    getPosts: (contextType: string, contextId: string): Promise<TournamentPostDto[]> =>
        requests.get(`/tournamentposts`, { params: { contextType, contextId } }),

    createPost: (contextType: string, contextId: string, content: string, visibility: string): Promise<TournamentPostDto> =>
        requests.post('/tournamentposts', { contextType, contextId, content, visibility }),

    deletePost: (postId: string): Promise<void> =>
        requests.del(`/tournamentposts/${postId}`),

    pinPost: (postId: string): Promise<void> =>
        requests.post(`/tournamentposts/${postId}/pin`, {}),

    addReply: (postId: string, content: string): Promise<void> =>
        requests.post(`/tournamentposts/${postId}/replies`, { content }),

    deleteReply: (postId: string, replyId: string): Promise<void> =>
        requests.del(`/tournamentposts/${postId}/replies/${replyId}`),

    reactToPost: (postId: string, type: string): Promise<void> =>
        requests.post(`/tournamentposts/${postId}/reactions`, { type }),

    // ── My Tournaments ────────────────────────────────────────────────────────────
    myOrganized: (): Promise<TournamentSearchResultDto[]> =>
        requests.get('/tournaments/mine/organized'),

    myParticipating: (): Promise<TournamentSearchResultDto[]> =>
        requests.get('/tournaments/mine/participating'),
};




const Playground = {
    // Feed
    getFeed: (params: FeedParams) =>
        axios.get<PaginatedResult<FeedItem[]>>('/playground/feed', {
            params: {
                pageNumber: params.pageNumber,
                pageSize: params.pageSize,
                radiusKm: params.radiusKm,
                sportId: params.sportId,
                showNeedOnePlayer: params.showNeedOnePlayer,
                showPublicGames: params.showPublicGames,
                showFriendActivity: params.showFriendActivity,
                showUserPosts: params.showUserPosts,
                showTournaments: params.showTournaments,   // ← NEW param
                friendsOnly: params.friendsOnly,
            }
        }).then(responseBody),

    createPost: (dto: CreatePostDto) =>
        requests.post<FeedItem>('/playground/posts', dto),

    deletePost: (id: string) =>
        requests.del<void>(`/playground/posts/${id}`),

    likePost: (id: string) =>
        requests.post<void>(`/playground/posts/${id}/like`, {}),

    notRelevant: (id: string) =>
        requests.post<void>(`/playground/posts/${id}/not-relevant`, {}),

    sharePost: (id: string) =>
        requests.post<void>(`/playground/posts/${id}/share`, {}),

    // Comments
    addComment: (dto: CreateCommentDto) =>
        requests.post<FeedComment>('/playground/comments', dto),

    likeComment: (id: string) =>
        requests.post<void>(`/playground/comments/${id}/like`, {}),

    // Urgency
    getUrgency: (params: UrgencyParams) => {
        const searchParams = new URLSearchParams();
        searchParams.append('pageNumber', String(params.pageNumber));
        searchParams.append('pageSize', String(params.pageSize));
        searchParams.append('radiusKm', String(params.radiusKm));
        searchParams.append('timeWindowHours', String(params.timeWindowHours));
        searchParams.append('friendsOnly', String(params.friendsOnly));
        (params.sportIds ?? []).forEach(id => searchParams.append('sportIds', id));

        return axios.get<PaginatedResult<FeedItem[]>>(
            `/playground/urgency?${searchParams.toString()}`
        ).then(responseBody);
    },

    // News
    getNews: (sport?: string) =>
        requests.get<NewsItem[]>(`/playground/news${sport ? `?sport=${sport}` : ''}`),

    // Influence
    getInfluenceProfile: (userId: string, historyDays: number = 30) =>
        requests.get<InfluenceProfile>(`/playground/influence/${userId}?historyDays=${historyDays}`),

    // Feed event trigger
    triggerFeedEvent: (gameId: string, trigger: number, triggeredByUserId: string) =>
        requests.post<void>('/playground/events/trigger', { gameId, trigger, triggeredByUserId }),

    getNewsComments: (newsId: string) =>
        requests.get<FeedComment[]>(`/playground/news/${newsId}/comments`),

    deleteComment: (commentId: string) =>
        requests.del(`/playground/comments/${commentId}`),

    // Deep-link helper 
    // Returns the page number that contains the given post (for deep-link navigation)
    getFeedPostPage: (postId: string, pageSize: number = 20) =>
        requests.get<{ postId: string; pageNumber: number; pageSize: number; indexInPage: number }>(
            `/playground/posts/${postId}/page?pageSize=${pageSize}`),
};

const GameVideoLinks = {
    list: (gameId: string) =>
        requests.get<GameVideoLink[]>(`/gamevideolinks/${gameId}`),

    add: (dto: AddGameVideoLinkDTO) =>
        requests.post<GameVideoLink>('/gamevideolinks', dto),

    delete: (videoLinkId: string) =>
        requests.del<void>(`/gamevideolinks/${videoLinkId}`),
};

const Courts = {
    /**
     * POST /courts/available-slots
     * Returns paginated available time slots for bookable court assets.
     */
    searchAvailableSlots: (request: AvailableCourtSlotsRequestDTO) =>
        requests.post<AvailableCourtSlotsResponseDTO>('/courts/available-slots', request),
};

const GameInvitations = {
    getInvitableFriends: (gameId: string) =>
        requests.get<InvitableFriend[]>(`/gameinvitations/${gameId}/invitable-friends`),

    getGameInvitations: (gameId: string) =>
        requests.get<GameInvitation[]>(`/gameinvitations/${gameId}`),

    sendInvitations: (gameId: string, userIds: string[]) =>
        requests.post<void>('/gameinvitations', { gameId, userIds })
};

const Account = {
    current: () => requests.get<User>('/account'),
    login: (user: UserFormValues) => requests.post<User>('/account/login', user),
    register: (user: UserFormValues) => requests.post<User>('/account/register', user),
    saveToken: (token: { token: string }) => requests.post<void>('/account/save-token', token),
    socialLogin: (socialData: SocialLoginData) => requests.post<User>('/account/social-login', socialData),
    forgotPassword: (email: { email: string }) => anonymousRequests.post<void>('/account/forgot-password', email),
    resetPassword: (resetData: ResetPasswordData) => anonymousRequests.post<void>('/account/reset-password', resetData),
    setLanguage: (language: LanguageDTO) => requests.post<void>('/account/language', language),
    deleteAccount: () => requests.del<void>('/account/delete-account')
};

const Companies = {
    listUserCompanies: (UserID: string) =>
        requests.get<Company[]>(`/company/${UserID}/companies`),
    create: (company: Company) => requests.post<Company>('/company', company),
    update: (company: Company) => requests.put<Company>(`/company/${company.id}`, company),
    details: (id: string) => requests.get<Company>(`/company/${id}`),
    delete: (id: string) => requests.del<void>(`/company/${id}`)
};

const CompanySportVenues = {
    list: () => requests.get<CompanySportVenue[]>('/companysportvenue'),
    listByCompany: (companyId: string) =>
        requests.get<CompanySportVenue[]>(`/companysportvenue/company/${companyId}`),
    details: (id: string) =>
        requests.get<CompanySportVenue>(`/companysportvenue/${id}`),
    create: (venue: CompanySportVenue) =>
        requests.post<CompanySportVenue>('/companysportvenue', venue),
    update: (venue: CompanySportVenue) =>
        requests.put<CompanySportVenue>(`/companysportvenue/${venue.id}`, venue),
    delete: (id: string) =>
        requests.del<void>(`/companysportvenue/${id}`)
};

const CompanySportVenueAssets = {
    list: () => requests.get<CompanySportVenueAsset[]>('/companysportvenueasset'),
    listByVenue: (venueId: string) =>
        requests.get<CompanySportVenueAsset[]>(`/companysportvenueasset/venue/${venueId}`),
    details: (id: string) =>
        requests.get<CompanySportVenueAsset>(`/companysportvenueasset/${id}`),
    create: (asset: CompanySportVenueAsset) =>
        requests.post<CompanySportVenueAsset>('/companysportvenueasset', asset),
    update: (asset: CompanySportVenueAsset) =>
        requests.put<CompanySportVenueAsset>(`/companysportvenueasset/${asset.id}`, asset),
    delete: (id: string) =>
        requests.del<void>(`/companysportvenueasset/${id}`),

    //Partition Rules
    addPartitionRule: (assetId: string, rule: Omit<PartitionRule, 'id'>) =>
        requests.post<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/partition-rules`, rule),
    deletePartitionRule: (assetId: string, ruleId: string) =>
        requests.del<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/partition-rules/${ruleId}`),

    // Pricing Rules
    addPricingRule: (assetId: string, rule: Omit<PricingRule, 'id'>) =>
        requests.post<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/pricing-rules`, rule),
    updatePricingRule: (assetId: string, rule: PricingRule) =>
        requests.put<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/pricing-rules/${rule.id}`, rule),
    deletePricingRule: (assetId: string, ruleId: string) =>
        requests.del<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/pricing-rules/${ruleId}`),

    // Discount Rules
    addDiscountRule: (assetId: string, rule: Omit<DiscountRule, 'id'>) =>
        requests.post<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/discount-rules`, rule),
    updateDiscountRule: (assetId: string, rule: DiscountRule) =>
        requests.put<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/discount-rules/${rule.id}`, rule),
    deleteDiscountRule: (assetId: string, ruleId: string) =>
        requests.del<CompanySportVenueAsset>(`/companysportvenueassets/${assetId}/discount-rules/${ruleId}`),
};

export const Reports = {
    occupancy: (params: ReportQueryParams) =>
        requests.get<OccupancyReportDTO>('/reporting/occupancy', { params: toReportParams(params) }),

    revenue: (params: ReportQueryParams) =>
        requests.get<RevenueReportDTO>('/reporting/revenue', { params: toReportParams(params) }),

    payments: (params: ReportQueryParams) =>
        requests.get<PaymentsReportDTO>('/reporting/payments', { params: toReportParams(params) }),

    lostRevenue: (params: ReportQueryParams) =>
        requests.get<LostRevenueReportDTO>('/reporting/lost-revenue', { params: toReportParams(params) }),

    clients: (params: ReportQueryParams) =>
        requests.get<ClientsReportDTO>('/reporting/clients', { params: toReportParams(params) }),

    bookingsOverview: (params: ReportQueryParams) =>
        requests.get<BookingsOverviewReportDTO>('/reporting/bookings-overview', {
            params: toReportParams(params),
        }),
};


function toReportParams(p: ReportQueryParams) {
    return {
        startDate: p.startDate.toISOString(),
        endDate: p.endDate.toISOString(),
        utcOffsetMinutes: -new Date().getTimezoneOffset(), // e.g. +60 for UTC+1
        ...(p.companySportVenueId && { companySportVenueId: p.companySportVenueId }),
        ...(p.assetIds?.length && { assetIds: p.assetIds }),
    };
}



const Friends = {
    search: (searchTerm: string, maxResults: number = 20) =>
        requests.get<Friend[]>(`/friend/search?searchTerm=${searchTerm}&maxResults=${maxResults}`),
    list: (userId: string, predicate: string) =>
        requests.get<FriendDto[]>(`/friend/${userId}?predicate=${predicate}`)
};

const FriendRequests = {
    send: (receiverId: string) =>
        requests.post(`/friendrequests/${receiverId}`, {}),
    accept: (requestId: string) =>
        requests.post(`/friendrequests/${requestId}/accept`, {}),
    reject: (requestId: string) =>
        requests.post(`/friendrequests/${requestId}/reject`, {}),
    cancel: (requestId: string) =>
        requests.del(`/friendrequests/${requestId}/cancel`),
    unfriend: (userId: string) =>
        requests.del(`/friendrequests/${userId}/unfriend`),
    getStatus: (userId: string) =>
        requests.get<FriendshipStatus>(`/friendrequests/status/${userId}`),
    getPending: () =>
        requests.get<FriendRequest[]>('/friendrequests/pending'),
    getFriends: (userId: string) =>
        requests.get<Profile[]>(`/friendrequests/friends/${userId}`)
};

const Messages = {
    // Returns boolean directly, not wrapped
    checkFriendship: (userId: string) =>
        requests.get<boolean>(`/messages/check-friendship/${userId}`),

    // These also return unwrapped values
    getConversations: (searchTerm?: string) =>
        requests.get<any[]>(`/messages/conversations${searchTerm ? `?searchTerm=${searchTerm}` : ''}`),

    getConversation: (conversationId: string) =>
        requests.get<any>(`/messages/${conversationId}`),

    markAsRead: (conversationId: string) =>
        requests.post<void>(`/messages/read/${conversationId}`, {}),

    getMessages: (conversationId: string, skip = 0, take = 50) =>
        requests.get<MessageDTO[]>(
            `/messages/${conversationId}/list?skip=${skip}&take=${take}`
        ),

    deleteMessage: (messageId: string) =>
        requests.del<void>(`/messages/${messageId}`),
};

const UserRatings = {
    getUserRatings: (userId: string, sportId?: string) => {
        const params = new URLSearchParams();
        if (sportId) params.append('sportId', sportId);

        return requests.get<UserRatingResponse>(
            `/userratings/${userId}${params.toString() ? `?${params.toString()}` : ''}`
        );
    },

    submitRating: (rating: SubmitRatingDto) =>
        requests.post<void>('/userratings', rating),

    updateCommentApproval: (dto: UpdateCommentApprovalDto) =>
        requests.put<void>('/userratings/comment-approval', dto)
};

const Notifications = {
    getUserPreferences: () => requests.get<UserNotificationPreference[]>('/notifications/preferences'),
    updatePreference: (preference: NotificationPreferenceDto) =>
        requests.put<void>('/notifications/preferences', preference),
    updateMultiplePreferences: (preferences: NotificationPreferenceDto[]) =>
        requests.put<void>('/notifications/preferences/bulk', preferences),
    getChannels: () => requests.get<NotificationChannel[]>('/notifications/channels'),
    getEventTypes: () => requests.get<NotificationEventType[]>('/notifications/event-types'),
};

// Calendar API endpoints for axios
const Calendar = {
    // Get calendar data (events, resources, statuses)
    list: (params?: CalendarEventQueryParams) => {
        const queryString = params ? '?' + new URLSearchParams(
            Object.entries(params)
                .filter(([_, value]) => value !== undefined)
                .map(([key, value]) => [key, String(value)])
        ).toString() : '';
        return requests.get<CalendarDataDTO>(`/calendar${queryString}`);
    },
    details: (id: string) => requests.get<CalendarEventDTO>(`/calendar/${id}`),
    create: (event: CreateCalendarEventDTO) => requests.post<CalendarEventDTO>('/calendar', event),
    update: (event: UpdateCalendarEventDTO) => requests.put<CalendarEventDTO>(`/calendar/${event.id}`, event),
    delete: (id: string) => requests.del<void>(`/calendar/${id}`),

    // Price calculation
    calculatePrice: (calculation: BookingPriceCalculation) =>
        requests.post<BookingPriceResult>('/calendar/calculate-price', calculation),

    createRecurringSeries: (data: CreateRecurringSeriesDTO): Promise<CreateRecurringSeriesResultDTO> =>
        requests.post<CreateRecurringSeriesResultDTO>('/calendar/recurring', data),

    editRecurringSeries: (seriesId: string, data: UpdateRecurringSeriesDTO): Promise<EditRecurringSeriesResultDTO> =>
        requests.put<EditRecurringSeriesResultDTO>(`/calendar/recurring/${seriesId}`, data),

    deleteRecurringSeries: (seriesId: string): Promise<DeleteRecurringSeriesResultDTO> =>
        requests.del<DeleteRecurringSeriesResultDTO>(`/calendar/recurring/${seriesId}`),

    detachFromSeries: (eventId: string): Promise<void> =>
        requests.put<void>(`/calendar/${eventId}/detach`, {}),

    deleteRecurringFromDate: (seriesId: string, fromDate: string): Promise<DeleteRecurringSeriesResultDTO> =>
        requests.del<DeleteRecurringSeriesResultDTO>(
            `/calendar/recurring/${seriesId}/from/${encodeURIComponent(fromDate)}`
        ),
};

const Profiles = {
    get: (userID: string) => requests.get<Profile>(`/profiles/${userID}`),

    uploadPhoto: (file: File) => {
        let formData = new FormData();
        formData.append('file', file, file.name);
        return axios.post('account/upload-profile-image', formData, {
            headers: { 'Content-type': 'multipart/form-data' }
        });
    },

    updateFollowing: (userID: string) => requests.post(`/userFollow/${userID}`, {}),
    listFollowings: (userID: string, predicate: string) =>
        requests.get<Profile[]>(`/userFollow/${userID}?predicate=${predicate}`),

    listGames: (userID: string, predicate: string) =>
        requests.get<UserGame[]>(`/profiles/${userID}/games?predicate=${predicate}`),

    updateUserLocation: (updateLocationDto: UpdateLocationDto) => requests.put<void>(`/profiles/location/`, updateLocationDto),

    updateFavoriteSports: (sportIds: string[]) =>
        requests.put<void>(`/profiles/favorite-sports`, { sportIds }),

    updateSportLevel: (sportId: string, level: number | null) =>
        requests.put('/profiles/sport-level', { sportId, level }),
}



const agent = {
    Games,
    Account,
    Profiles,
    Companies,
    CompanySportVenues,
    CompanySportVenueAssets,
    Calendar,
    Friends,
    FriendRequests,
    Messages,
    UserRatings,
    Notifications,
    GameInvitations,
    Courts,
    GameVideoLinks,
    Reports,
    Playground,
    Teams,
    TeamPosts,
    TeamMatches,
    Tournaments

};

export { agent, setupInterceptors };